import { useState } from 'react';
import {
  DialogRoot,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogCloseTrigger,
} from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { InputGroup } from '@/components/ui/input-group';
import { Field } from '@/components/ui/field';
import { Steps } from '@/components/ui/steps';
import { 
  Text, 
  Skeleton, 
  Stack, 
  Flex, 
  Button, 
  Input, 
  Icon, 
  Textarea,
  Box
} from '@chakra-ui/react';
import { 
  MagnifyingGlassIcon, 
} from '@heroicons/react/24/outline';
import useContentNames from '@/lib/hooks/use-content-names';
import useAIContentRequests from '@/lib/hooks/use-update-content-names';

interface ContentNamesStepsModalProps {
  isOpen: boolean;
  onClose: () => void;
  workspaceId: string;
  contentTypeId: string;
  onSuccess?: () => void;
}

interface ContentNameFormData {
  id: number;
  name: string;
  description: string;
  sport_type: string;
  country: string;
}

type StepType = 'select' | 'edit';

export default function ContentNamesStepsModal({
  isOpen,
  onClose,
  workspaceId,
  contentTypeId,
  onSuccess,
}: ContentNamesStepsModalProps) {
  const { contentNames, isLoading, error } = useContentNames(workspaceId, contentTypeId);
  const { createAIContentRequests, isLoading: isUpdating } = useAIContentRequests();
  
  const [currentStep, setCurrentStep] = useState<StepType>('select');
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState<ContentNameFormData[]>([]);

  const steps = [
    {
      title: 'Select Content',
      description: 'Choose content names to edit',
      status: currentStep === 'select' ? 'current' as const : selectedIds.length > 0 ? 'completed' as const : 'pending' as const,
    },
    {
      title: 'Edit Content',
      description: 'Update names and descriptions',
      status: currentStep === 'edit' ? 'current' as const : currentStep === 'select' ? 'pending' as const : 'completed' as const,
    },
  ];

  // Filter content names based on search query
  const filteredContentNames = Array.isArray(contentNames) 
    ? contentNames.filter(content => {
        if (!searchQuery.trim()) return true;
        const query = searchQuery.toLowerCase();
        return (
          content.name.toLowerCase().includes(query) ||
          content.description?.toLowerCase().includes(query) ||
          content.content_type?.toLowerCase().includes(query) ||
          content.sport_type?.toLowerCase().includes(query) ||
          content.country?.toLowerCase().includes(query)
        );
      })
    : [];

  const selectedContentNames = Array.isArray(contentNames) 
    ? contentNames.filter(content => selectedIds.includes(content.id))
    : [];

  const handleCheckboxChange = (contentId: number, isChecked: boolean) => {
    const newSelectedIds = isChecked
      ? [...selectedIds, contentId]
      : selectedIds.filter(id => id !== contentId);
    
    setSelectedIds(newSelectedIds);
  };

  const handleSelectAll = () => {
    const allIds = filteredContentNames.map(content => content.id);
    setSelectedIds(allIds);
  };

  const handleDeselectAll = () => {
    setSelectedIds([]);
  };

  const handleNext = () => {
    if (currentStep === 'select' && selectedIds.length > 0) {
      // Initialize form data for selected items
      const initialData = selectedContentNames.map(content => ({
        id: content.id,
        name: content.name || '',
        sport_type: content.sport_type || '',
        country: content.country || '',
        description: content.description || '',
      }));
      setFormData(initialData);
      setCurrentStep('edit');
    }
  };

  const handleBack = () => {
    if (currentStep === 'edit') {
      setCurrentStep('select');
    }
  };

  const handleInputChange = (id: number, field: 'name' | 'description' | 'sport_type' | 'country', value: string) => {
    setFormData(prev => 
      prev.map(item => 
        item.id === id 
          ? { ...item, [field]: value }
          : item
      )
    );
  };

  const validateFormData = () => {
    return formData.every(item => {
      const trimmedName = item.name.trim();
      const trimmedDescription = item.description.trim();
      
      // Name is required and must not be empty
      if (!trimmedName || trimmedName.length === 0) {
        return false;
      }
      
      // Name must be at least 2 characters
      if (trimmedName.length < 2) {
        return false;
      }
      
      // Description is required and must not be empty
      if (!trimmedDescription || trimmedDescription.length === 0) {
        return false;
      }
      
      // Description must be at least 10 characters
      if (trimmedDescription.length < 10) {
        return false;
      }
      
      return true;
    });
  };

  const getFieldValidation = (item: ContentNameFormData) => {
    const trimmedName = item.name.trim();
    const trimmedDescription = item.description.trim();
    
    return {
      name: {
        isinvalid: !trimmedName || trimmedName.length < 2,
        errorText: !trimmedName ? 'Name is required' : trimmedName.length < 2 ? 'Name must be at least 2 characters' : ''
      },
      description: {
        isinvalid: !trimmedDescription || trimmedDescription.length < 10,
        errorText: !trimmedDescription ? 'Description is required' : trimmedDescription.length < 10 ? 'Description must be at least 10 characters' : ''
      }
    };
  };

  const handleSubmit = async () => {
    // Validate all fields
    if (!validateFormData()) {
      return;
    }

    const newAIContentRequests = formData.map(item => ({
      workspace_id: parseInt(workspaceId),
      country: item.country.trim(),
      sport_type: item.sport_type.trim(),
      content_type_id: parseInt(contentTypeId),
      name: item.name.trim(),
      description: item.description.trim(),
      assistant_name: 'genai',
    }));

    const success = await createAIContentRequests(newAIContentRequests);
    if (success) {
      onSuccess?.();
      onClose();
      // Reset state
      setCurrentStep('select');
      setSelectedIds([]);
      setFormData([]);
      setSearchQuery('');
    }
  };

  const handleClose = () => {
    if (!isLoading && !isUpdating) {
      onClose();
      // Reset state
      setCurrentStep('select');
      setSelectedIds([]);
      setFormData([]);
      setSearchQuery('');
    }
  };

  const hasValidationErrors = !validateFormData();

  const renderSelectStep = () => (
    <>
      <Flex gap={2} mb={4} wrap="wrap">
        <Button 
          size="sm" 
          variant="outline" 
          onClick={handleSelectAll}
          disabled={filteredContentNames.length === 0}
        >
          Select All ({filteredContentNames.length})
        </Button>
        <Button 
          size="sm" 
          variant="outline" 
          onClick={handleDeselectAll}
          disabled={selectedIds.length === 0}
        >
          Deselect All
        </Button>
      </Flex>

      <InputGroup
        w={'full'}
        mb={4}
        startElement={<Icon as={MagnifyingGlassIcon} w={4} h={4} />}
      >
        <Input
          placeholder="Search content names..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </InputGroup>
      
      <Stack gap={3} maxH="400px" overflowY="auto">
        {filteredContentNames.length === 0 ? (
          <Text color="gray.500">
            {searchQuery ? `No content names found matching "${searchQuery}"` : 'No content names found'}
          </Text>
        ) : (
          filteredContentNames.map((content) => (
            <Checkbox
              key={content.id}
              checked={selectedIds.includes(content.id)}
              onCheckedChange={(details) => handleCheckboxChange(content.id, details.checked === true)}
            >
              <Flex direction="column" align="start">
                <Text fontSize="sm" fontWeight="medium">
                  {content.name}
                </Text>
                {content.description && (
                  <Text fontSize="xs" color="gray.600" mt={1}>
                    {content.description}
                  </Text>
                )}
              </Flex>
            </Checkbox>
          ))
        )}
      </Stack>
      
      <Text fontSize="sm" fontWeight="medium" color="gray.700" mt={4}>
        {selectedIds.length} selected
        {searchQuery && filteredContentNames.length !== (Array.isArray(contentNames) ? contentNames.length : 0) && (
          <span> • {filteredContentNames.length} of {Array.isArray(contentNames) ? contentNames.length : 0} shown</span>
        )}
      </Text>
    </>
  );

  const renderEditStep = () => (
    <>
      <Text color="gray.600" mb={4}>
        Edit the name and description for {selectedContentNames.length} selected content items.
      </Text>
      
      <Stack gap={4} maxH="400px" overflowY="auto">
        {formData.map((content, index) => (
          <Flex
            key={content.id}
            direction="column"
            p={4}
            border="1px solid"
            borderColor="gray.200"
            borderRadius="md"
            bg="gray.50"
          >
            <Text fontSize="xs" color="gray.500" mb={2}>
              Item {index + 1} of {formData.length}
            </Text>
            
            <Stack gap={3}>
              {(() => {
                const validation = getFieldValidation(content);
                return (
                  <>
                    <Field 
                      label="Name" 
                      required 
                      errorText={validation.name.errorText}
                      helperText={`${content.name.trim().length}/2 characters minimum`}
                    >
                      <Input
                        value={content.name}
                        onChange={(e) => handleInputChange(content.id, 'name', e.target.value)}
                        placeholder="Enter content name (min 2 characters)"
                      />
                    </Field>
                    
                    <Field 
                      label="Description" 
                      required
                      errorText={validation.description.errorText}
                      helperText={`${content.description.trim().length}/10 characters minimum`}
                    >
                      <Textarea
                        value={content.description}
                        onChange={(e) => handleInputChange(content.id, 'description', e.target.value)}
                        placeholder="Enter content description (min 10 characters)"
                        rows={3}
                      />
                    </Field>
                  </>
                );
              })()}
            </Stack>
          </Flex>
        ))}
      </Stack>
      
      {hasValidationErrors && (
        <Text color="red.500" fontSize="sm" mt={2}>
          Please fix all validation errors above. Names must be at least 2 characters and descriptions must be at least 10 characters.
        </Text>
      )}
    </>
  );

  return (
    <DialogRoot open={isOpen} onOpenChange={(e) => !e.open && handleClose()}>
      <DialogContent maxW="800px">
        <DialogHeader>
          <DialogTitle>Create AI Content Requests</DialogTitle>
          <DialogCloseTrigger />
        </DialogHeader>
        
        <DialogBody>
          <Box mb={6}>
            <Steps steps={steps} />
          </Box>

          {error && (
            <Text color="red.500" mb={4}>
              {error}
            </Text>
          )}
          
          {isLoading ? (
            <Stack gap={3}>
              {Array.from({ length: 5 }).map((_, index) => (
                <Flex key={index} align="center" gap={3}>
                  <Skeleton height="20px" width="20px" />
                  <Skeleton height="16px" width="200px" />
                </Flex>
              ))}
            </Stack>
          ) : (
            <>
              {currentStep === 'select' && renderSelectStep()}
              {currentStep === 'edit' && renderEditStep()}
            </>
          )}
        </DialogBody>
        
        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={currentStep === 'edit' ? handleBack : handleClose}
            disabled={isLoading || isUpdating}
          >
            {currentStep === 'edit' ? 'Back' : 'Cancel'}
          </Button>
          
          {currentStep === 'select' ? (
            <Button 
              colorPalette="brand" 
              onClick={handleNext}
              disabled={selectedIds.length === 0}
            >
              Next ({selectedIds.length} selected)
            </Button>
          ) : (
            <Button 
              colorPalette="brand" 
              onClick={handleSubmit}
              loading={isUpdating}
              disabled={hasValidationErrors}
            >
              Create AI Content Requests
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
}
